import { ListType } from 'types/apollo/globalTypes';

/**
 * @property value {string} Key that CSV columns map to
 * @property regex {RegExp} Used for auto-mapping of uploaded CSV headers
 *
 * NOTE: These should match the Mappings type in lead-platform
 */

const normalizeForMatching = (s: string) =>
  s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();

export const leadListMappings = [
  { name: 'Ext Created', value: 'extCreated', regex: /\bext(?:\s*created)?\b/i },
  { name: 'Created', value: 'created', regex: /(?<!\bext )\bcreated\b/i },
  { name: 'Email', value: 'email', regex: /\b(email|mail)\b/i },
  { name: 'Ext Lead Id', value: 'extLeadId', regex: /\b(lead\s*id|lead|id)\b/i },
  { name: 'First Name', value: 'firstName', regex: /\b(first|given)\b|^fn$/i },
  { name: 'Full Name', value: 'fullName', regex: /\bfull\b|\bfull\s*name\b/i },
  { name: 'IP Address', value: 'ipAddress', regex: /\bip(\s*address)?\b/i },
  { name: 'Last Name', value: 'lastName', regex: /\b(last|sur(?:name)?)\b|^ln$/i },
  { name: 'Locality', value: 'locality', regex: /\bcity\b/i },
  {
    name: 'Phone Number',
    value: 'phoneNumber',
    regex: /(telephone|phone|number|tel|cell|contact)/i,
  },
  { name: 'Postal Code', value: 'postalCode', regex: /\b(post|zip|area(\s*code)?)\b/i },
  { name: 'Region', value: 'region', regex: /\b(region|state)\b|^st$/i },
  { name: 'Source', value: 'source', regex: /\b(source|campaign|src)\b/i },
  { name: 'Timezone', value: 'timezone', regex: /\b(time|zone|tz)\b/i },
];

const EXACT_HEADER_ALIASES: Record<string, string> = {
  city: 'locality',
  company: 'source',
};

const FORCE_UNMAP_HEADERS = new Set<string>(['location', 'locale']);

const VALUE_BY_LOWER = leadListMappings.reduce<Record<string, string>>((acc, m) => {
  acc[m.value.toLowerCase()] = m.value;
  return acc;
}, {});

export const LS_CACHE_NAME = 'list-column-mapping-cache';

type ShortId = string;
type CacheV2 = Record<ShortId, Record<string, Record<string, string>>>;

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const isProbableShortId = (k: string) => k.includes('-') || /^[a-z0-9]{6,}$/i.test(k);

function migrateToShortIdOnly(parsed: unknown): CacheV2 {
  const out: CacheV2 = {};
  if (!isObject(parsed)) return out;

  for (const companyId of Object.keys(parsed)) {
    const byCampaign = (parsed as Record<string, unknown>)[companyId];
    if (!isObject(byCampaign)) continue;

    for (const key of Object.keys(byCampaign)) {
      if (key === '_ALL_CAMPAIGNS_') continue;
      if (!isProbableShortId(key)) continue;

      const byType = (byCampaign as Record<string, unknown>)[key];
      if (!isObject(byType)) continue;

      out[key] = { ...(out[key] || {}), ...(byType as Record<string, Record<string, string>>) };
    }
  }

  for (const maybeShortId of Object.keys(parsed)) {
    if (!isProbableShortId(maybeShortId)) continue;
    const val = (parsed as Record<string, unknown>)[maybeShortId];
    if (isObject(val)) {
      out[maybeShortId] = {
        ...(out[maybeShortId] || {}),
        ...(val as Record<string, Record<string, string>>),
      };
    }
  }

  return out;
}

function readCache(): CacheV2 {
  const raw = localStorage.getItem(LS_CACHE_NAME);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);

    const isShortIdOnly =
      isObject(parsed) &&
      Object.keys(parsed).length > 0 &&
      Object.keys(parsed).every(isProbableShortId) &&
      Object.values(parsed).every(isObject);

    if (isShortIdOnly) {
      return parsed as CacheV2;
    }

    const migrated = migrateToShortIdOnly(parsed);
    localStorage.setItem(LS_CACHE_NAME, JSON.stringify(migrated));
    return migrated;
  } catch {
    return {};
  }
}

function writeCache(cache: CacheV2) {
  localStorage.setItem(LS_CACHE_NAME, JSON.stringify(cache));
}

export const getCachedMappings = (
  listType: ListType,
  campaignShortId?: string | null,
): Record<string, string> => {
  if (!campaignShortId) return {};
  const cache = readCache();
  const byType = cache[campaignShortId]?.[listType] || {};
  return { ...byType };
};

export const saveCachedMappings = (
  listType: ListType,
  mappings: Record<string, string>,
  campaignShortId?: string | null,
) => {
  if (!campaignShortId) return;
  const cache = readCache();
  cache[campaignShortId] ||= {};
  const existing = cache[campaignShortId][listType] || {};
  cache[campaignShortId][listType] = { ...existing, ...mappings };
  writeCache(cache);
};

export const predictLeadListMappings = (
  headers: string[],
  listType?: ListType,
  campaignShortId?: string | null,
): Record<string, string> => {
  if (!headers || headers.length === 0) return {};

  const cachedMappingRaw = listType ? getCachedMappings(listType, campaignShortId) : {};
  const cachedMapping = Object.fromEntries(
    Object.entries(cachedMappingRaw).filter(([k]) => headers.includes(k)),
  );

  const initialMapping: Record<string, string> = {};
  for (const originalHeader of headers) {
    const normalizedLower = originalHeader.trim().toLowerCase();

    if (FORCE_UNMAP_HEADERS.has(normalizedLower)) {
      initialMapping[originalHeader] = 'unmapped';
      continue;
    }

    const aliasTarget = EXACT_HEADER_ALIASES[normalizedLower];
    if (aliasTarget) {
      initialMapping[originalHeader] = aliasTarget;
      continue;
    }

    const canonical = VALUE_BY_LOWER[normalizedLower];
    if (canonical) {
      initialMapping[originalHeader] = canonical;
      continue;
    }
  }

  const mapping: Record<string, string> = { ...initialMapping, ...cachedMapping };

  for (const spec of leadListMappings) {
    for (const originalHeader of headers) {
      if (mapping[originalHeader] === undefined || mapping[originalHeader] === 'unmapped') {
        const haystack = normalizeForMatching(originalHeader);
        if (spec.regex.test(haystack)) {
          mapping[originalHeader] = spec.value;
        }
      }
    }
  }

  const mappedValues = Object.values(mapping);
  const hasAnyName =
    mappedValues.includes('firstName') ||
    mappedValues.includes('lastName') ||
    mappedValues.includes('fullName');

  if (!hasAnyName) {
    const genericNameHeader = headers.find((h) => /name/i.test(h));
    if (
      genericNameHeader &&
      (mapping[genericNameHeader] === undefined || mapping[genericNameHeader] === 'unmapped')
    ) {
      mapping[genericNameHeader] = 'fullName';
    }
  }

  return mapping;
};
